<template>
  <s-modal
    class="group-form-modal"
    v-model="showModal"
    ok-confirm-message-code="MC011"
    cancel-confirm-message-code="MC006"
    size="md"
    :is-loading="loading"
    :title="title"
    :ok-title="$t('general.confirm')"
    :ok-disabled="$v.$invalid"
    :ok-promise="save"
    :confirm-type="confirmationTypes.YES_NO"
    @show="onShow"
    @cancel="cancel"
  >
    <b-form-group
      v-if="fields.situation.visible"
      v-slot="{ ariaDescribedby }"
    >
      <b-form-radio-group
        id="groupFormSituation"
        v-model="situation"
        :options="situationOptions"
        :aria-describedby="ariaDescribedby"
        :state="$bsStateValidator($v.situation.$error)"
        :disabled="loading"
        @input="$v.situation.$touch()"
      ></b-form-radio-group>
    </b-form-group>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.description')}*`"
      label-class="font-weight-bold"
    >
      <b-input
        id="groupFormDescription"
        class="form-control"
        v-model="$v.description.$model"
        v-bind="getInputOptions('description')"
        :disabled="loading"
        @input="$v.description.$touch()"
        @blur="$v.description.$touch"
      />
      <b-form-invalid-feedback v-if="$v.description.$error">
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.associated-contact')}*`"
      label-class="font-weight-bold"
    >
      <s-select
        v-model="$v.associatedContact.$model"
        id="groupFormAssociatedContactSelect"
        value-field="id"
        text-field="name"
        wrapped-item
        :state="$v.associatedContact.$error"
        :options="associatedContacts"
        :disabled="loading"
        @input="$v.associatedContact.$touch()"
        @hidden="$v.associatedContact.$touch()"
      />
      <b-form-invalid-feedback v-if="$v.associatedContact.$error">
      </b-form-invalid-feedback>
    </b-form-group>
    <s-fieldset
      id="groupFormFieldset"
      class="group-form-modal__fieldset form-group"
      :legend="$t('fields.engineering.cooling-maintenance.automatic-maintenance-notice')"
    >
      <b-form-group
        :label="`${$t('fields.engineering.cooling-maintenance.advance-notice-maintenance')}*`"
        label-class="font-weight-bold"
      >
        <s-number-input
          id="groupFormadvanceNoticeMaintenance"
          class="group-form-modal__input"
          v-model="$v.advanceNoticeMaintenance.$model"
          :numeric-options="configureNumericOptionsPrecision()"
          :validation="$v.advanceNoticeMaintenance"
          :invalid="$v.advanceNoticeMaintenance.$error"
          :disabled="loading"
          @blur-native-proxy="$v.advanceNoticeMaintenance.$touch()"
        />
        <b-form-invalid-feedback v-if="$v.advanceNoticeMaintenance.$error">
          {{ getMA086(fields.advanceNoticeMaintenance) }}
        </b-form-invalid-feedback>
      </b-form-group>
    </s-fieldset>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.operating-time-maintenance')}*`"
      label-class="font-weight-bold"
    >
      <s-number-input
        id="groupFormOperatingTimeMaintenance"
        class="group-form-modal__input"
        v-model="$v.operatingTimeMaintenance.$model"
        :numeric-options="configureNumericOptionsPrecision()"
        :validation="$v.operatingTimeMaintenance"
        :invalid="$v.operatingTimeMaintenance.$error"
        :disabled="loading"
        @blur-native-proxy="$v.operatingTimeMaintenance.$touch()"
      />
      <b-form-invalid-feedback v-if="$v.operatingTimeMaintenance.$error">
        {{ getMA086(fields.operatingTimeMaintenance) }}
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.installation-date')}*`"
      label-class="font-weight-bold"
    >
      <s-input-date-picker
        id="groupFormInstallationDate"
        class="group-form-modal__input"
        v-model="installationDateProxy"
        :time-zone="dateOnlyTimeZone"
        :min-date="minimumDate"
        :max-date="maximumDate"
        :state="$bsStateValidator($v.installationDate.$error)"
        :disabled="loading"
        @input="$v.installationDate.$touch()"
        @blur-native-proxy="$v.installationDate.$touch()"
      />
      <b-form-invalid-feedback
        v-if="$v.installationDate.$error"
        class="d-block"
      >
        {{ getMA101(fields.installationDate) }}
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.start-up-date')}*`"
      label-class="font-weight-bold"
    >
      <s-input-date-picker
        id="groupFormOperationEntryDate"
        class="group-form-modal__input"
        v-model="operationEntryDateProxy"
        :time-zone="dateOnlyTimeZone"
        :min-date="minimumDate"
        :max-date="maximumDate"
        :state="$bsStateValidator($v.operationEntryDate.$error)"
        :disabled="loading"
        @input="$v.operationEntryDate.$touch()"
        @blur-native-proxy="$v.operationEntryDate.$touch()"
      />
      <b-form-invalid-feedback
        v-if="$v.operationEntryDate.$error && !$v.operationEntryDate.minDate"
        class="d-block"
      >
        {{ getMA104() }}
      </b-form-invalid-feedback>
      <b-form-invalid-feedback
        v-else-if="$v.operationEntryDate.$error && $v.operationEntryDate.minDate"
        class="d-block"
      >
        {{ getMA101(fields.operationEntryDate) }}
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.last-maintenance-date')}*`"
      label-class="font-weight-bold"
      v-if="fields.lastMaintenanceDate.visible"
    >
      <s-input-date-picker
        id="groupFormLastMaintenanceDate"
        class="group-form-modal__input"
        v-model="lastMaintenanceDateProxy"
        :time-zone="dateOnlyTimeZone"
        :min-date="minimumDate"
        :max-date="maximumDate"
        :state="$bsStateValidator($v.lastMaintenanceDate.$error)"
        :disabled="loading"
        @input="$v.lastMaintenanceDate.$touch()"
        @blur-native-proxy="$v.lastMaintenanceDate.$touch()"
      />
      <b-form-invalid-feedback
        v-if="$v.lastMaintenanceDate.$error && !$v.lastMaintenanceDate.minDate"
        class="d-block"
      >
        {{ getMA104() }}
      </b-form-invalid-feedback>
      <b-form-invalid-feedback
        v-else-if="$v.lastMaintenanceDate.$error && $v.lastMaintenanceDate.minDate"
        class="d-block"
      >
        {{ getMA101(fields.lastMaintenanceDate) }}
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.total-operating-time')}*`"
      label-class="font-weight-bold"
      v-if="fields.totalOperationTime.visible"
    >
      <s-number-input
        id="groupFormTotalOperationTime"
        class="group-form-modal__input"
        v-model="$v.totalOperationTime.$model"
        :numeric-options="configureNumericOptionsPrecision()"
        :validation="$v.totalOperationTime"
        :invalid="$v.totalOperationTime.$error"
        :disabled="loading"
        @blur-native-proxy="$v.totalOperationTime.$touch()"
      />
      <b-form-invalid-feedback v-if="$v.totalOperationTime.$error">
        {{ getMA086(fields.totalOperationTime) }}
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.time-since-last-maintenance')}*`"
      label-class="font-weight-bold"
      v-if="fields.timeSinceLastMaintenance.visible"
    >
      <s-number-input
        id="groupFormTimeSinceLastMaintenance"
        class="group-form-modal__input"
        v-model="$v.timeSinceLastMaintenance.$model"
        :numeric-options="configureNumericOptionsPrecision()"
        :validation="$v.timeSinceLastMaintenance"
        :invalid="$v.timeSinceLastMaintenance.$error"
        :disabled="loading"
        @blur-native-proxy="$v.timeSinceLastMaintenance.$touch()"
      />
      <b-form-invalid-feedback v-if="$v.timeSinceLastMaintenance.$error">
        {{ getMA086(fields.timeSinceLastMaintenance) }}
      </b-form-invalid-feedback>
    </b-form-group>
    <b-form-group
      :label="`${$t('fields.engineering.cooling-maintenance.performed-maintenance-amount')}*`"
      label-class="font-weight-bold"
      v-if="fields.performedMaintenanceAmount.visible"
    >
      <s-number-input
        id="groupFormPerformedMaintenanceAmount"
        class="group-form-modal__input"
        v-model="$v.performedMaintenanceAmount.$model"
        :numeric-options="configureNumericOptionsPrecision()"
        :validation="$v.performedMaintenanceAmount"
        :invalid="$v.performedMaintenanceAmount.$error"
        :disabled="loading"
        @blur-native-proxy="$v.performedMaintenanceAmount.$touch()"
      />
      <b-form-invalid-feedback v-if="$v.performedMaintenanceAmount.$error">
        {{ getMA086(fields.performedMaintenanceAmount) }}
      </b-form-invalid-feedback>
    </b-form-group>
  </s-modal>
</template>

<script>
import { CoolingMaintenanceSituations } from 'sigma-mappings';
import confirmationTypes from '@/constants/confirmationTypes';
import modalTypes
  from '@/domain/enums/installation/engineering/coolingMaintenance/groupFormModalTypes';
import AssetService from '@/domain/services/installation/assetInfo/registrationData';
import Service
  from '@/domain/services/installation/engineering/coolingMaintenance/coolingMaintenanceService';
import groupFields from '@/domain/data/installation/engineering/coolingMaintenance/groupFields';
import validations from '@/domain/data/installation/engineering/coolingMaintenance/validations';
import eventBus from '@/services/globalEventBus';
import events from '@/domain/events/engineering';
import {
  formatter as dateTimeFormatter,
  getInitialDate,
  getStartOfDay,
  setToStartOfDay,
} from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';

const dateOnlyTimeZone = timezones.SYSTEM_DATE_ONLY;
const currentDate = getStartOfDay({ timeZone: dateOnlyTimeZone });

export default {
  name: 'GroupFormModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    modalType: {
      type: String,
      default: '',
      validator: value => Object.values(modalTypes).includes(value),
    },
    groupId: {
      type: String,
    },
  },
  data: () => ({
    loading: false,
    associatedContacts: [],
    situation: CoolingMaintenanceSituations.NEW,
    description: '',
    associatedContact: null,
    advanceNoticeMaintenance: null,
    operatingTimeMaintenance: null,
    installationDate: currentDate,
    operationEntryDate: null,
    lastMaintenanceDate: currentDate,
    totalOperationTime: null,
    timeSinceLastMaintenance: null,
    performedMaintenanceAmount: null,
    minimumDate: getInitialDate({ timeZone: dateOnlyTimeZone }),
    maximumDate: currentDate,
    moneyInputKey: 0,
    confirmationTypes,
    dateOnlyTimeZone,
  }),
  validations() {
    const dates = {
      installationDate: this.installationDate,
      operationEntryDate: this.operationEntryDate,
      lastMaintenanceDate: this.lastMaintenanceDate,
    };
    return validations(this.isUpdating, this.fields, dates);
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    installationDateProxy: {
      get() {
        return this.installationDate;
      },
      set(value) {
        this.installationDate = this.getProxyDate(value);
      },
    },
    operationEntryDateProxy: {
      get() {
        return this.operationEntryDate;
      },
      set(value) {
        this.operationEntryDate = this.getProxyDate(value);
      },
    },
    lastMaintenanceDateProxy: {
      get() {
        return this.lastMaintenanceDate;
      },
      set(value) {
        this.lastMaintenanceDate = this.getProxyDate(value);
      },
    },
    situationOptions() {
      return [
        {
          text: this.$t('fields.engineering.cooling-maintenance.new'),
          value: CoolingMaintenanceSituations.NEW,
        },
        {
          text: this.$t('fields.engineering.cooling-maintenance.used'),
          value: CoolingMaintenanceSituations.USED,
        },
      ];
    },
    fields() {
      return groupFields(this.isUpdating, this.situation);
    },
    isNew() {
      return this.modalType === modalTypes.INSERT
        && this.situation === CoolingMaintenanceSituations.NEW;
    },
    isUsed() {
      return this.modalType === modalTypes.INSERT
        && this.situation === CoolingMaintenanceSituations.USED;
    },
    isUpdating() {
      return this.modalType === modalTypes.UPDATE;
    },
    title() {
      const titleByModalType = {
        [modalTypes.INSERT]: this.$t('engineering.cooling-maintenance.insert-group'),
        [modalTypes.UPDATE]: this.$t('engineering.cooling-maintenance.update-group'),
      };
      return titleByModalType[this.modalType] || undefined;
    },
  },
  methods: {
    onShow() {
      const promises = [this.getAssociatedContacts(), this.getAssetInfo()];
      Promise.allSettled(promises)
        .then(this.getGroup);
    },
    configureNumericOptionsPrecision() {
      return {
        decimalPlaces: 0,
      };
    },
    clearFields() {
      this.situation = CoolingMaintenanceSituations.NEW;
      this.description = '';
      this.associatedContact = null;
      this.advanceNoticeMaintenance = null;
      this.operatingTimeMaintenance = null;
      this.installationDate = currentDate;
      this.operationEntryDate = null;
      this.lastMaintenanceDate = currentDate;
      this.totalOperationTime = null;
      this.timeSinceLastMaintenance = null;
      this.performedMaintenanceAmount = null;
      this.$v.$reset();
    },
    getProxyDate(value) {
      if (!value) return value;
      return setToStartOfDay(value, { timeZone: this.dateOnlyTimeZone });
    },
    cancel() {
      this.clearFields();
      this.showModal = false;
    },
    getAssociatedContacts() {
      this.loading = true;
      const { assetModuleId } = this.$route.params;
      return Service.getAssociatedContacts(assetModuleId)
        .then((response) => {
          const nullOption = { name: this.$t('general.select'), id: null };
          this.associatedContacts = [nullOption, ...response];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getAssetInfo() {
      this.loading = true;
      const { assetId } = this.$route.params;
      return AssetService.getRegistrationAssetData(assetId)
        .then(({ operationEntry }) => {
          this.operationEntryDateProxy = operationEntry || currentDate;
          this.installationDateProxy = operationEntry || currentDate;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setGroupModel(data) {
      this.situation = data.situation;
      this.description = data.description;
      this.associatedContact = data.associatedContact;
      this.advanceNoticeMaintenance = data.advanceNoticeMaintenance;
      this.operatingTimeMaintenance = data.operatingTimeMaintenance;
      this.installationDateProxy = data.installationDate;
      this.operationEntryDateProxy = data.operationEntryDate;
      this.lastMaintenanceDateProxy = data.lastMaintenanceDate;
      this.totalOperationTime = data.totalOperationTime;
      this.timeSinceLastMaintenance = data.timeSinceLastMaintenance;
      this.performedMaintenanceAmount = data.performedMaintenanceAmount;
      this.moneyInputKey += 1;
    },
    getGroup() {
      const { assetModuleId } = this.$route.params;

      if (this.isUpdating) {
        this.loading = true;
        Service.getGroupById(assetModuleId, this.groupId)
          .then(this.setGroupModel)
          .finally(() => {
            this.loading = false;
          });
      }
    },
    getInputOptions(fieldName) {
      return {
        min: this.fields[fieldName].minValue,
        max: this.fields[fieldName].maxValue,
        maxLength: this.fields[fieldName].maxLength,
        state: this.$bsStateValidator(this.$v[fieldName].$error),
      };
    },
    getMA086(field) {
      return this.$t('messages.MA086.message', {
        minValue: this.$n(field.minValue),
        maxValue: this.$n(field.maxValue),
      });
    },
    getMA101(field) {
      return this.$t('messages.MA101.message', {
        0: this.dateFormatter(field.minValue),
        1: this.dateFormatter(field.maxValue),
      });
    },
    getMA104() {
      return this.$t('messages.MA104.message');
    },
    dateFormatter(isoDate) {
      if (!isoDate) return '';
      return dateTimeFormatter(isoDate, formats.DATE_SHORT);
    },
    save() {
      const payload = {
        situation: this.isUpdating ? null : this.situation,
        description: this.description,
        associatedContact: this.associatedContact,
        advanceNoticeMaintenance: this.advanceNoticeMaintenance,
        operatingTimeMaintenance: this.operatingTimeMaintenance,
        installationDate: this.installationDate,
        operationEntryDate: this.operationEntryDate,
        lastMaintenanceDate: this.isUpdating || this.isNew ? null : this.lastMaintenanceDate,
        totalOperationTime: this.isNew ? null : this.totalOperationTime,
        timeSinceLastMaintenance: this.isNew ? null : this.timeSinceLastMaintenance,
        performedMaintenanceAmount: this.isUpdating ? null : this.performedMaintenanceAmount,
      };

      const callback = () => {
        this.$pushAlert({ code: 'MS032' });
        eventBus.emit(events.refreshDataTab);
        this.cancel();
      };
      const { assetModuleId } = this.$route.params;

      this.loading = true;

      if (this.isUpdating) {
        return Service.updateGroupById(assetModuleId, this.groupId, payload)
          .then(callback)
          .finally(() => {
            this.loading = false;
          });
      }

      return Service.setGroup(this.$route.params.assetModuleId, payload)
        .then(callback)
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    modalType(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.clearFields();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
.group-form-modal
  &__fieldset
    padding-top: 10px
    padding-bottom: 10px
    max-height: 175px
    overflow-y: auto

  &__input
    max-width: 30%

  ::v-deep
    .modal-dialog
      max-width: 600px

    .custom-control.custom-control-inline.custom-radio
      padding-right: 3em

.manage-fonts_16px
  .group-form-modal
    &__fieldset
      padding-bottom: 15px
      max-height: 190px

    ::v-deep
      .modal-dialog
        max-width: 640px

.manage-fonts_18px
  .group-form-modal
    &__fieldset
      padding-bottom: 20px
      max-height: 205px

    ::v-deep
      .modal-dialog
        max-width: 720px
</style>
